<template>
  <div class="cart-drawer critical-component-hide">
    <progress-indicator
      v-if="gwp.enabled && typeof gwp.threshold === 'number'"
      v-show="cart.item_count > 0"
      class="cart-drawer__progress"
      :threshold="gwp.threshold"
      :total="cartTotalExcludingGift"
      :is-special-condition="isGiftInCart"
      :threshold-unmet-message="$string('cart.free_gift.threshold_unmet')"
      :threshold-met-message="$string('cart.free_gift.threshold_met')"
      :special-condition-message="$string('cart.free_gift.added_to_cart')"
      @threshold-met="handleThresholdMet"
    />

    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem, index) of cartItems"
          :key="getCartItemKey(lineItem, index)"
        >
          <!-- `shouldHighlightLastItem` ab test add to cart notice -->
          <line-item
            class="cart-drawer__line-item"
            :class="
              shouldHighlightLastItem(lineItem, index === cartItems.length - 1)
            "
            :line-item="lineItem"
            :gift-added="gwp.added"
          />

          <divider-line />
        </template>

        <gift-with-purchase
          v-if="gwp.enabled && gwp.isThresholdMet && !isGiftInCart"
          :product="gwp.product"
        />

        <transition name="fade">
          <div
            v-if="overlay.isActive"
            class="cart-drawer__widgets"
          >
            <rebuy-cart-upsell
              v-for="block of rebuyWidgets"
              :key="block.id"
              :widget-id="block.settings.id"
            />
          </div>
        </transition>
      </div>

      <footer
        v-show="cart.item_count > 0"
        class="cart-drawer__footer"
      >
        <cart-gift-note />

        <!-- ab test add to cart notice -->
        <cart-summary :message="liquid.textCheckout" />
        <a
          v-if="liquid.noticeCart"
          class="cart-notice__toggle icon-button"
          :href="$variable('routes.cart.url')"
          @click="handleCartToggle"
          v-text="liquid.textToggle"
        />
        <!--
          <button
          ref="toggleNotifyCart"
          class="cart-notice__toggle"
          type="button"
          @click="removeClassNotice"
          v-text="liquid.textToggle"
          />
        -->
        <!-- end ab test add to cart notice -->
      </footer>
    </form>

    <div
      v-else
      class="cart-drawer__empty"
    >
      <div class="cart-drawer__empty-wrapper">
        <p
          class="text-body-m-regular text-body-l-regular-desktop"
          v-text="$string('cart.empty.drawer_text')"
        />
        <btn
          v-if="liquid.continueShopping"
          :label="$string('cart.continue_shopping')"
          :url="liquid.continueShopping"
          class="mt-m mt-l-desktop"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import CartSummary from '~async/cart-summary/cart-summary'
import CartGiftNote from '~async/cart-gift-note/cart-gift-note'
import DividerLine from '~async/utils/divider-line/divider-line'
import LineItem from '~async/line-item/line-item'
import ProgressIndicator from '~async/progress-indicator/progress-indicator'
import GiftWithPurchase from '~async/gift-with-purchase/gift-with-purchase'
import RebuyCartUpsell from '~async/rebuy-cart-upsell/rebuy-cart-upsell'

import Btn from '~global/btn/btn'

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartSummary,
    CartGiftNote,
    DividerLine,
    GiftWithPurchase,
    LineItem,
    ProgressIndicator,
    RebuyCartUpsell,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        continueShopping: '',
        textToggle: '', // ab test add to cart notice
        textCheckout: '', // ab test add to cart notice
        noticeCart: false, // ab test add to cart notice
        gwp: {
          product: false,
          threshold: 0,
          enabled: false,
          isThresholdMet: false,
        },
        blocks: [],
      }),
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      body: document.body, // ab test add to cart notice
      notifyTimeout: null, // ab test add to cart notice
      gwp: {
        added: false,
        ...this.liquid.gwp,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
      cartHistory: (state) => state.cart.history, // ab test add to cart notice
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      giftLineItem: 'cart/getGiftLineItem',
      cartTotalExcludingGift: 'cart/getCartTotalExcludingGift',
    }),

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty =
            typeof addedItem === 'string' ? cartItem.key : cartItem.id

          return cartItemProperty === addedItem
        })

        if (!match) {
          placeholders.push(false)
        }
      })

      return [...this.cart.items, ...placeholders]
    },

    /**
     * Compute if gift item is in cart.
     * @returns {Boolean}
     */
    isGiftInCart() {
      return Boolean(this.giftLineItem)
    },

    /**
     * Compute rebuy widget blocks
     * @returns {Array}
     */
    rebuyWidgets() {
      return this.liquid.blocks.filter((block) => block.type === 'rebuy_widget')
    },
  },

  watch: {
    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      // ab test add to cart notice
      if (
        !this.liquid?.noticeCart &&
        this.cartHistory[0]?.action === 'addToCart'
      ) {
        this.showNofity()
      }
      // end ab test add to cart notice

      if (!this.overlay.isActive) {
        return
      }

      if (!this.liquid.noticeCart) {
        this.overlay.update({ currentElement: document.activeElement })
      }
    },

    /**
     * Watch gift line item getter to set gift just added message
     */
    giftLineItem(newItem, oldItem) {
      if (oldItem || !newItem) {
        return
      }

      this.gwp.added = true

      setTimeout(() => {
        this.gwp.added = false
      }, 5 * 1000)
    },

    'overlay.isActive'() {
      if (!this.liquid.noticeCart && this.overlay.isActive) {
        this.overlay.update({ currentElement: document.activeElement })
      }
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }
    if (!this.liquid?.noticeCart && this.cartHistory[0]) {
      this.showNofity()
    }

    if (!this.liquid.noticeCart) {
      this.overlay.update()
    }
  },

  methods: {

    // ab test add to cart notice
    removeClassNotice() {
      setTimeout(() => {
        this.body.classList.remove('notice-show')
      }, 500)
    },

    toggleBetweenNotifyCart(idSetTimeout) {
      if (this.notifyTimeout) {
        clearTimeout(idSetTimeout)
      }

      /*
      this.$refs.toggleNotifyCart.removeEventListener('click',
        this.toggleBetweenNotifyCart
      )
      */
    },

    showNofity() {
      this.ubicationNotify()

      this.notifyTimeout = setTimeout(() => {
        if (!this.body.classList.contains('notice-active') && this.body.classList.contains('test-noticecart-on')) {
          this.overlay.close()
        }
        setTimeout(() => {
          this.body.classList.remove('notice-show')
        }, 250)
      }, 5000)

      /*
      this.$refs.toggleNotifyCart.addEventListener('click',
        this.toggleBetweenNotifyCart(this.notifyTimeout)
      )
      */
    },

    ubicationNotify() {
      this.body.classList.add('notice-show')
      const header = document.querySelector('.site-header-group')
      const cartNotice = document.querySelector('.cart-notice')

      if (header && cartNotice) {
        const headerHeight = header.offsetHeight
        const secondChildHeight = header.children[1]?.offsetHeight

        const adjustCartNoticeTop = () => {
          const documentDataset = document.documentElement?.dataset?.headerState

          if (documentDataset === 'open') {
            document.documentElement.style.setProperty(
              '--cart-notice-top',
              `${headerHeight}px`,
            )
          } else if (documentDataset === 'collapsed') {
            document.documentElement.style.setProperty(
              '--cart-notice-top',
              '0px',
            )
          } else if (documentDataset === 'minimised') {
            document.documentElement.style.setProperty(
              '--cart-notice-top',
              `${secondChildHeight}px`,
            )
          } else {
            document.documentElement.style.setProperty(
              '--cart-notice-top',
              `${headerHeight}px`,
            )
          }
        }

        adjustCartNoticeTop()
        window.addEventListener('resize', adjustCartNoticeTop)
      }
    },

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @returns {String}
     */
    shouldHighlightLastItem(item, lastIndex) {
      let newClass = ''
      if (item.id === this.cartHistory[0]?.items[0]?.id && lastIndex) {
        newClass = 'last-item'
      } else if (lastIndex) {
        newClass = 'last-item'
      }
      return newClass
    },
    // end ab test add to cart notice

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      removeItem: 'cart/updateItem',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },

    /**
     * Handle threshold met event.
     * @param {Boolean} isMet - Is threshold met.
     * @returns {Boolean}
     */
    handleThresholdMet(isMet) {
      this.gwp.isThresholdMet = isMet
      if (!isMet) {
        this.handleRemoveGiftItem()
      }
    },

    /**
     * Handle remove gift item.
     */
    handleRemoveGiftItem() {
      if (!this.giftLineItem) {
        return
      }
      this.removeItem(this.giftLineItem.key)
    },
  },

}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
